import React from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import Image from 'next/image';

interface ISavingsModal {
  closeSavingsModal: () => void;
  cityState?: string;
  downloadUrl?: string;
  cityElectric: {
    annual: {
      savings: number;
    };
    ebuilt: {
      cost: number;
    };
    traditional: {
      cost: number;
    };
  } | null;
  cityGas: {
    annual: {
      savings: number;
    };
    ebuilt: {
      cost: number;
    };
    traditional: {
      cost: number;
    };
  } | null;
}

interface IUtilSection {
  utilName: string;
  ebuiltCost: string;
  traditionalCost: string;
  annualSavings: string;
}

const SavingsModal = ({
  closeSavingsModal,
  cityState,
  downloadUrl,
  cityElectric = null,
  cityGas = null,
}: ISavingsModal) => {
  const currencyFormat = (num: Number) => {
    return `$${num?.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`;
  };

  const numUtils = cityElectric && cityGas ? 2 : 1;

  const UtilSection = ({
    utilName,
    ebuiltCost,
    traditionalCost,
    annualSavings,
  }: IUtilSection) => (
    <div>
      <h4 className="mx-auto mb-5 mt-2.5 rounded-xl bg-clayton-blue p-[7px] text-center text-[1.25rem] text-white smDesktop:mb-[5px] smDesktop:p-[5px] smDesktop:text-[1rem]">
        {utilName} Utilities
      </h4>
      <div className="mb-5 mt-2.5 grid grid-cols-2 gap-4 smDesktop:mb-2.5">
        <div className="flex flex-col items-center justify-center rounded-xl bg-white py-5 text-center smDesktop:pb-0 smDesktop:pt-2">
          <Image
            src="/public-assets/ebuilt-logo.svg"
            alt="eBuilt Logo"
            height={75}
            width={114}
            className="mb-[-20px] h-[75px] smDesktop:mt-[-10px] smDesktop:h-20"
          />
          <span className="w-1/2 rounded-xl text-center font-extrabold smDesktop:mt-[-7px] smDesktop:w-[80%] smDesktop:text-[13px] smDesktop:font-semibold">
            Home
          </span>
          <span className="text-[24px] font-semibold text-clayton-blue lg:text-[13px] smDesktop:pb-4">
            {ebuiltCost}
          </span>
        </div>
        <div className="flex flex-col items-center justify-center rounded-xl bg-white py-5 text-center smDesktop:py-0">
          <span className="w-1/2 rounded-xl text-center font-extrabold lg:text-[13px] smDesktop:w-[80%] smDesktop:font-semibold">
            Traditional Off-Site Home
          </span>
          <span className="text-[24px] font-semibold text-clayton-blue lg:text-[13px]">
            {traditionalCost}
          </span>
        </div>
      </div>
      <div className="mb-5 flex flex-col rounded-xl bg-clayton-blue py-[7px] text-center text-white smDesktop:mb-[15px] smDesktop:p-[3px]">
        <span className="text-[14px] font-semibold smDesktop:text-[12px]">
          Estimated Annual Savings:
        </span>
        <span className="text-[24px] font-extrabold smDesktop:text-[20px]">
          {annualSavings}
        </span>
      </div>
    </div>
  );

  return (
    <div
      className={classNames(
        'w-full rounded-lg bg-[#e1ecf8] pb-[30px] smDesktop:absolute smDesktop:left-[5%] smDesktop:top-[15%] smDesktop:h-[340px]',
        {
          ['smDesktop:w-[550px]']: numUtils === 2,
          ['smDesktop:w-[450px]']: numUtils === 1,
        }
      )}
    >
      <div className="w-full pr-[7px] pt-[7px] text-end">
        <button
          className="h-5 w-5 rounded-full border-none bg-clayton-blue text-[12px] font-extrabold text-white"
          onClick={closeSavingsModal}
        >
          X
        </button>
      </div>
      <div
        className={classNames('mx-auto max-w-[394px] smDesktop:max-w-[450px]', {
          ['smDesktop:w-1/2']: numUtils === 1,
        })}
      >
        <h3 className="my-2.5 rounded-xl bg-clayton-blue p-[7px] text-center text-[1.75rem] text-white smDesktop:m-[unset] smDesktop:p-[5px] smDesktop:text-[1.25rem]">
          {cityState}
        </h3>
      </div>
      <div
        className={classNames(
          'mx-auto flex max-w-[394px] flex-col gap-4 md:flex-row',
          {
            ['md:max-w-[590px] smDesktop:max-w-[450px]']: numUtils === 2,
            ['smDesktop:w-1/2']: numUtils === 1,
          }
        )}
      >
        {cityElectric && (
          <UtilSection
            utilName="Electric"
            ebuiltCost={currencyFormat(cityElectric?.ebuilt?.cost)}
            traditionalCost={currencyFormat(cityElectric?.traditional?.cost)}
            annualSavings={currencyFormat(cityElectric?.annual?.savings)}
          />
        )}
        {cityGas && (
          <UtilSection
            utilName="Gas"
            ebuiltCost={currencyFormat(cityGas?.ebuilt?.cost)}
            traditionalCost={currencyFormat(cityGas?.traditional?.cost)}
            annualSavings={currencyFormat(cityGas?.annual?.savings)}
          />
        )}
      </div>
      {downloadUrl && (
        <div className="flex cursor-pointer items-center justify-center text-center">
          <Image
            src="/public-assets/fact-sheet.svg"
            alt="Fact Sheet Logo"
            height={32}
            width={32}
            className="mt-[5px] smDesktop:h-[25px]"
          />
          <Link
            href={downloadUrl}
            target="_blank"
            rel="noopenner noreferrer"
            className="text-[24px] font-semibold visited:text-clayton-blue hover:text-clayton-blue focus:text-clayton-blue"
          >
            <span>Download PDF</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default React.memo(SavingsModal);
